import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Facebook, Instagram, Youtube, Mail, MapPin } from "lucide-react";
import NewsletterForm from "@/components/forms/NewsletterForm";

const SOCIALS = [
  { label: "LinkedIn", href: "https://www.linkedin.com/company/tratou-energy/", icon: Linkedin, id: "linkedin" },
  { label: "Facebook", href: "https://www.facebook.com/share/18UPbpuRfc/", icon: Facebook, id: "facebook" },
  { label: "Instagram", href: "https://www.instagram.com/tratouenergy?igsh=bjZqbmluMGluczdm", icon: Instagram, id: "instagram" },
  { label: "TikTok", href: "https://www.tiktok.com/@tratou.energy?_r=1&_t=ZS-97kuRQp9g05", icon: TikTokIcon, id: "tiktok" },
  { label: "YouTube", href: "https://youtube.com/@tratouenergy?si=EobcQ_Akym7YSlhY", icon: Youtube, id: "youtube" },
  { label: "X", href: "https://x.com/TratouEnergy", icon: XIcon, id: "x" },
];

function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M19.6 6.32a5.66 5.66 0 0 1-3.52-1.22 5.66 5.66 0 0 1-2.15-4.1h-3.4v13.75a2.7 2.7 0 1 1-1.92-2.6V8.5a6.15 6.15 0 1 0 5.32 6.1V9.28a8.94 8.94 0 0 0 5.67 1.98V7.83a5.6 5.6 0 0 1 0-1.51Z" />
    </svg>
  );
}

function XIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M18.244 2H21.5l-7.53 8.61L23 22h-6.828l-5.34-6.98L4.76 22H1.5l8.06-9.21L1 2h6.914l4.83 6.4L18.244 2Zm-1.196 18h1.87L7.02 3.86H5.02L17.048 20Z" />
    </svg>
  );
}

export default function Footer() {
  return (
    <footer className="relative border-t border-white/10 bg-tratou-deep mt-32" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-10">
        <div className="col-span-2 lg:col-span-2">
          <img src="/tratou-logo.png" alt="Tratou Energy" className="h-8 w-auto mb-6" />
          <p className="text-tratou-dim text-sm leading-relaxed max-w-sm">
            Portable Renewable Energy. Anywhere. Anytime. We provide Mobile Battery Energy Storage Systems and Energy-as-a-Service for industries where the grid cannot reach.
          </p>

          <address className="mt-6 not-italic text-sm text-tratou-dim space-y-1" data-testid="footer-address">
            <p className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-tratou-green mt-0.5 flex-shrink-0" />
              <span>
                Tratou Energy powered by Tratou Commercial Brokers CO LLC<br />
                114-66, Hamsah Building, Zabeel Street<br />
                Dubai, United Arab Emirates
              </span>
            </p>
            <p className="flex items-center gap-2 pt-1">
              <Mail className="w-4 h-4 text-tratou-green flex-shrink-0" />
              <a href="mailto:info@tratouenergy.com" className="hover:text-tratou-green transition" data-testid="footer-email">
                info@tratouenergy.com
              </a>
            </p>
          </address>

          <NewsletterForm />
        </div>
        <Col title="Company" links={[["/", "Home"], ["/investors", "Investors"], ["/careers", "Careers"], ["/contact", "Contact"]]} />
        <Col title="Products" links={[["/products", "All Products"], ["/products#mbess-106", "MBESS-106"], ["/products#mbess-431", "MBESS-431"], ["/products#mbess-863", "MBESS-863"]]} />
        <Col title="Solutions" links={[["/eaas", "Energy-as-a-Service"], ["/roi", "ROI Calculator"], ["/industries", "Industries"], ["/blog", "Resources"]]} />
        <Col title="Legal" links={[["/privacy", "Privacy Policy"], ["/terms", "Terms"], ["/contact", "Support"]]} />
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-tratou-faint">© 2026 Tratou Energy. All rights reserved.</p>
          <div className="flex items-center gap-4">
            {SOCIALS.map(({ label, href, icon: Icon, id }) => (
              <a
                key={id}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                title={label}
                className="text-tratou-dim hover:text-tratou-green transition"
                data-testid={`social-${id}`}
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
            <a href="mailto:info@tratouenergy.com" aria-label="Email" className="text-tratou-dim hover:text-tratou-green transition" data-testid="social-email"><Mail className="w-4 h-4" /></a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function Col({ title, links }) {
  return (
    <div>
      <h4 className="eyebrow-dim mb-4">{title}</h4>
      <ul className="space-y-2">
        {links.map(([href, label]) => (
          <li key={href + label}>
            <Link to={href} className="text-sm text-white/80 hover:text-tratou-green transition">{label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
